import { getAdapterWorkflowStepState, type AdapterWorkflowStepStatus } from "./persistence";
import { buildAdapterProgressSegments } from "./stepProgress";
import type { AdapterStepEvaluationContext } from "./stepTypes";
import {
  getAdapterEffectiveStepCompletion,
  getAdapterStepAccess,
  getAdapterStepOutstanding,
  getAdapterStepStatuses,
  type AdapterWizardStep,
} from "./wizard";

/** The step to show: the one with `stepKey`, else `fallbackIndex`, kept within the steps. */
export const resolveAdapterCurrentStepIndex = (
  steps: AdapterWizardStep[],
  stepKey: string | undefined,
  fallbackIndex = 0,
): number => {
  const keyedIndex = stepKey ? steps.findIndex((step) => step.key === stepKey) : -1;
  return Math.min(Math.max(keyedIndex >= 0 ? keyedIndex : fallbackIndex, 0), Math.max(steps.length - 1, 0));
};

export interface AdapterWorkflowEvaluationInput extends AdapterStepEvaluationContext {
  steps: AdapterWizardStep[];
  currentStepIndex: number;
  stepStatus?: AdapterWorkflowStepStatus[];
  /** Steps the user has been on; they open steps that only need another visited. */
  visitedSteps?: string[];
  manualStepCompletions?: Record<string, boolean>;
  /**
   * Completion to use instead of checking the steps, for callers without live resource inspections (the
   * sidebar uses the saved step status).
   */
  automaticCompletion?: Record<string, boolean>;
  errorsByKey?: Record<string, unknown[]>;
  connectionTestError?: string;
  /** Whether the workflow has started; an unstarted workflow shows every step as incomplete. */
  started?: boolean;
  /** Steps whose live state can't be worked out (their resources failed to load): their saved state stands. */
  savedStepKeys?: string[];
}

export interface AdapterWorkflowEvaluation {
  /** Completion from the steps' own rules (or the given automatic completion). */
  automaticCompletion: Record<string, boolean>;
  /** Completion including steps the user marked complete. */
  completion: Record<string, boolean>;
  access: ReturnType<typeof getAdapterStepAccess>;
  segments: ReturnType<typeof buildAdapterProgressSegments>;
}

/**
 * Completion, access and progress for an adapter workflow. The workflow panel, the Adapters page and the
 * sidebar all use this, so they agree on what each step's state is.
 */
export const evaluateAdapterWorkflow = ({
  steps,
  currentStepIndex,
  stepStatus = [],
  visitedSteps = [],
  manualStepCompletions = {},
  automaticCompletion: givenAutomaticCompletion,
  errorsByKey,
  connectionTestError,
  started,
  savedStepKeys = [],
  ...context
}: AdapterWorkflowEvaluationInput): AdapterWorkflowEvaluation => {
  // Saved completion has no block statuses; live evaluation derives completion from them, except for steps whose
  // live state can't be worked out, which keep their saved state.
  const liveStatuses = givenAutomaticCompletion ? undefined : getAdapterStepStatuses(steps, context);
  const saved = new Set(savedStepKeys);
  const statuses = liveStatuses &&
    Object.fromEntries(Object.entries(liveStatuses).filter(([key]) => !saved.has(key)));
  const automaticCompletion = givenAutomaticCompletion ??
    Object.fromEntries(steps.map((step) => [
      step.key,
      saved.has(step.key)
        ? getAdapterWorkflowStepState(stepStatus, step.key) === "success"
        : liveStatuses?.[step.key] === "success",
    ]));
  // Given completion (saved status) is trusted as is; otherwise aggregate steps (finish) are re-checked with it.
  const completion = getAdapterEffectiveStepCompletion(
    steps,
    automaticCompletion,
    manualStepCompletions,
    givenAutomaticCompletion ? undefined : context,
  );
  const access = getAdapterStepAccess(steps, { ...context, currentStepIndex, visitedSteps, completion });
  const segments = buildAdapterProgressSegments({
    steps,
    inspections: context.inspections,
    statuses,
    outstanding: liveStatuses ? getAdapterStepOutstanding(steps, context) : undefined,
    access,
    completion,
    automaticCompletion,
    manualCompletions: manualStepCompletions,
    stepStatus,
    errorsByKey,
    currentStepKey: steps[currentStepIndex]?.key,
    connectionTestError,
    started,
  });
  return { automaticCompletion, completion, access, segments };
};
